import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Mail,
  MessageSquare,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  MessageCircle,
} from 'lucide-react';
import UsageCard from './usage-card';
import { useParams } from 'react-router';
import { useCredits } from '@/hooks/useCredits';
import { useEffect } from 'react';
import { creditStore } from '@/state/CriditStore';
import { observer } from 'mobx-react-lite';
import { AuthStore } from '@/state/AuthenticationStore';
import Spinner from '@/components/atoms/Spinner';

interface ActivityItem {
  _id: string;
  actionType: 'email' | 'sms' | 'chat' | 'credit_purchase' | 'refund';
  description: string;
  credits: number;
  timestamp: string;
  details?: string;
  status: 'completed' | 'pending' | 'failed';
}

interface RawCreditHistoryItem {
  _id: string;
  actionType: ActivityItem['actionType'];
  credits: number;
  createdAt: string;
}

const mapCreditsHistory = (data: unknown): ActivityItem[] => {
  if (!Array.isArray(data)) return [];

  return (data as RawCreditHistoryItem[]).map(
    (item: RawCreditHistoryItem): ActivityItem => {
      let description = '';
      let details = '';
      const status: ActivityItem['status'] = 'completed';

      switch (item.actionType) {
        case 'email':
          description = 'Email credits used';
          details = `${item.credits} credits used for sending emails`;
          break;
        case 'sms':
          description = 'SMS credits used';
          details = `${item.credits} credits used for sending SMS messages`;
          break;
        case 'chat':
          description = 'Chat credits used';
          details = `${item.credits} credits used for AI chat interactions`;
          break;
        case 'credit_purchase':
          description = 'Credits purchased';
          details = `Purchased ${item.credits} credits`;
          break;
        case 'refund':
          description = 'Credits refunded';
          details = `Refunded ${item.credits} credits`;
          break;
        default:
          description = 'Credit activity';
          details = `${item.credits} credits affected`;
      }

      return {
        _id: item._id,
        actionType: item.actionType,
        credits: item.credits,
        timestamp: item.createdAt,
        description,
        details,
        status,
      };
    },
  );
};

function getActivityIcon(type: ActivityItem['actionType']) {
  switch (type) {
    case 'email':
      return <Mail className='h-4 w-4' />;
    case 'sms':
      return <MessageSquare className='h-4 w-4' />;
    case 'chat':
      return <MessageCircle className='h-4 w-4' />;
    case 'credit_purchase':
    case 'refund':
      return <Plus className='h-4 w-4' />;
    default:
      return <ArrowUpRight className='h-4 w-4' />;
  }
}

function getActivityColor(type: ActivityItem['actionType']) {
  switch (type) {
    case 'email':
      return 'bg-secondary/10 text-secondary/70 border-secondary/20';
    case 'sms':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'chat':
      return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'credit_purchase':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'refund':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
}

function formatTimestamp(timestamp: string) {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInHours = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60),
  );

  if (diffInHours < 1) {
    return 'Just now';
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  } else if (diffInHours < 48) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  }
}

const CreditActivityLog = () => {
  const params = useParams();
  const { getCreditHistory } = useCredits();

  const creditsHistory = creditStore.credits;
  const currentBalance =
    AuthStore.user_workspace_info?.active_workspace?.workspace?.credits;

  const activities = mapCreditsHistory(creditsHistory);
  const emailsUsed = activities
    .filter(a => a.actionType === 'email' && a.credits)
    .reduce((sum, a) => sum + Math.abs(a.credits), 0);

  const smsUsed = activities
    .filter(a => a.actionType === 'sms' && a.credits)
    .reduce((sum, a) => sum + Math.abs(a.credits), 0);

  const chatsUsed = activities
    .filter(a => a.actionType === 'chat' && a.credits)
    .reduce((sum, a) => sum + Math.abs(a.credits), 0);

  const totalUsedCredits = emailsUsed + smsUsed + chatsUsed;

  useEffect(() => {
    if (params?.workspaceId) {
      getCreditHistory(params?.workspaceId);
    }
  }, []);

  if (creditStore.isLoading) {
    <div className='h-full w-full flex flex-row justify-center items-center'>
      <Spinner size={40} color={'#033EB5'} />
    </div>;
  }

  return (
    <div className='w-full mx-auto space-y-10 h-full flex flex-col'>
      {/* Credit Overview Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
        <UsageCard
          title='Total Balance'
          icon={ArrowUpRight}
          value={currentBalance || 0}
          iconBg='bg-slate-100'
          iconColor='text-slate-700'
          borderColor='border-slate-200'
          valueColor='text-slate-700'
          description={`Total credits used on performing actions ${totalUsedCredits}`}
        />

        <UsageCard
          title='Email Credits'
          icon={Mail}
          value={activities
            .filter(a => a.actionType === 'email' && a.credits)
            .reduce((sum, a) => sum + Math.abs(a.credits), 0)}
          iconBg='bg-secondary/20'
          iconColor='text-secondary/80'
          borderColor='border-secondary/30'
          valueColor='text-secondary/80'
          description='Credits used on sending emails'
        />

        <UsageCard
          title='SMS Credits'
          icon={MessageSquare}
          value={activities
            .filter(a => a.actionType === 'sms' && a.credits)
            .reduce((sum, a) => sum + Math.abs(a.credits), 0)}
          iconBg='bg-green-100'
          iconColor='text-green-700'
          borderColor='border-green-200'
          valueColor='text-green-700'
          description='Credits used on sending SMSs'
        />

        <UsageCard
          title='Chat Credits'
          icon={MessageCircle}
          value={activities
            .filter(a => a.actionType === 'chat' && a.credits)
            .reduce((sum, a) => sum + Math.abs(a.credits), 0)}
          iconBg='bg-purple-100'
          iconColor='text-purple-700'
          borderColor='border-purple-200'
          valueColor='text-purple-700'
          description='Credits used on sending chats'
        />
      </div>

      {/* Activity Log */}
      <Card className='shadow-none flex-1 overflow-y-auto max-h-[500px]'>
        <CardHeader>
          <CardTitle> Credit History Log</CardTitle>
          <CardDescription className='text-md'>
            Your recent credit usage
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {activities.length ? (
            activities.map((activity, index) => (
              <div key={activity._id}>
                <div className='flex items-start gap-4'>
                  <div
                    className={`p-2 rounded-full border ${getActivityColor(
                      activity.actionType,
                    )}`}
                  >
                    {getActivityIcon(activity.actionType)}
                  </div>

                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center justify-between gap-2'>
                      <div className='flex items-center gap-2'>
                        <p className='font-medium text-md'>
                          {activity.description}
                        </p>
                        {activity.status === 'failed' && (
                          <Badge variant='destructive' className='text-sm'>
                            Failed
                          </Badge>
                        )}
                        {activity.status === 'pending' && (
                          <Badge variant='secondary' className='text-sm'>
                            Pending
                          </Badge>
                        )}
                      </div>
                      <div className='flex items-center gap-2'>
                        <span className={`font-semibold text-md text-red-600`}>
                          {'-'}
                          {activity.credits}
                        </span>
                        <ArrowDownRight className='h-3 w-3 text-red-600' />
                      </div>
                    </div>

                    {activity.details && (
                      <p className='text-sm text-muted-foreground mt-1'>
                        {activity.details}
                      </p>
                    )}

                    <p className='text-sm text-muted-foreground mt-1'>
                      {formatTimestamp(activity.timestamp)}
                    </p>
                  </div>
                </div>

                {index < activities.length - 1 && (
                  <Separator className='mt-4' />
                )}
              </div>
            ))
          ) : (
            <div className='mt-2 flex h-60 items-center justify-center rounded-lg bg-gray-50 px-4 py-4 text-center font-medium uppercase text-gray-400'>
              No Credit Logs Available!
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
export default observer(CreditActivityLog);
